import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { apiClient } from '../../utils/apiClient';
// Animations
const fadeUp = keyframes `
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const fadeIn = keyframes `
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
// Scroll animation mixin
const scrollReveal = css `
  opacity: ${props => props.$isVisible ? 1 : 0};
  transform: translateY(${props => props.$isVisible ? '0' : '40px'});
  transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
`;
// Page Container
const PageWrapper = styled.div `
  min-height: 100vh;
  background: var(--bg-primary);
  overflow-x: hidden;
`;
// Hero Section
const HeroSection = styled.section `
  position: relative;
  padding: 120px 24px 80px;
  text-align: center;
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);

  @media (max-width: 768px) {
    padding: 80px 16px 60px;
  }
`;
const HeroContent = styled.div `
  max-width: 800px;
  margin: 0 auto;
  animation: ${fadeUp} 0.8s ease-out;
`;
const HeroTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 400;
  color: var(--text-primary);
  margin-bottom: 24px;
  letter-spacing: 0.02em;
  line-height: 1.2;

  span {
    color: var(--accent-gold, #B8860B);
  }
`;
const HeroSubtitle = styled.p `
  font-family: 'DM Sans', 'Poppins', sans-serif;
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: var(--text-secondary);
  line-height: 1.8;
  max-width: 600px;
  margin: 0 auto;
`;
// Main Content
const ContentWrapper = styled.main `
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 80px;

  @media (max-width: 768px) {
    padding: 0 16px 60px;
  }
`;
// Contact Grid
const ContactGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 48px;
  align-items: start;
  ${scrollReveal}

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;
// Form Section
const FormSection = styled.div `
  animation: ${fadeUp} 0.8s ease-out 0.2s backwards;
`;
const SectionLabel = styled.span `
  display: inline-block;
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: var(--accent-gold, #B8860B);
  margin-bottom: 12px;
`;
const SectionTitle = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 400;
  color: var(--text-primary);
  margin-bottom: 32px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 0;
    width: 60px;
    height: 2px;
    background: var(--accent-gold, #B8860B);
  }
`;
// Contact Form
const StyledForm = styled.form `
  background: var(--bg-secondary);
  padding: 40px;
  border: 1px solid var(--border-color);
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg,
      transparent,
      var(--accent-gold, #B8860B) 20%,
      var(--accent-gold, #B8860B) 80%,
      transparent
    );
  }

  &:hover {
    border-color: var(--border-color-hover);
    box-shadow: var(--shadow-lg);
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;
const FormRow = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;
const FormGroup = styled.div `
  margin-bottom: 20px;
`;
const Label = styled.label `
  display: block;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;
const Input = styled.input `
  width: 100%;
  padding: 14px 16px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.3s ease;

  &::placeholder {
    color: var(--text-tertiary);
  }

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(184, 134, 11, 0.1));
  }

  &:hover:not(:focus) {
    border-color: var(--border-color-hover);
  }
`;
const Textarea = styled.textarea `
  width: 100%;
  min-height: 140px;
  padding: 14px 16px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.3s ease;
  resize: vertical;

  &::placeholder {
    color: var(--text-tertiary);
  }

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(184, 134, 11, 0.1));
  }

  &:hover:not(:focus) {
    border-color: var(--border-color-hover);
  }
`;
const CheckboxWrapper = styled.div `
  margin: 24px 0;
`;
const CheckboxLabel = styled.label `
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  cursor: pointer;
  line-height: 1.5;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    accent-color: var(--accent-gold, #B8860B);
    cursor: pointer;
  }
`;
const SubmitButton = styled.button `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 40px;
  background: var(--accent-gold, #B8860B);
  color: white;
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    background: var(--accent-gold-dark, #8B6914);
    transform: translateY(-2px);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: var(--border-color);
    cursor: not-allowed;
    opacity: 0.6;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`;
// Sidebar
const Sidebar = styled.aside `
  display: flex;
  flex-direction: column;
  gap: 24px;
  animation: ${fadeUp} 0.8s ease-out 0.4s backwards;
`;
const InfoCard = styled.div `
  background: var(--bg-secondary);
  padding: 28px;
  border: 1px solid var(--border-color);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 0;
    background: var(--accent-gold, #B8860B);
    transition: height 0.4s ease;
  }

  &:hover {
    border-color: var(--border-color-hover);
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);

    &::before {
      height: 100%;
    }
  }
`;
const CardTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;
const CardText = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.7;
  margin: 8px 0;
`;
const OfficeHours = styled.div `
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
`;
const HoursLabel = styled.span `
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--accent-gold, #B8860B);
`;
const HoursValue = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  color: var(--text-primary);
  margin-top: 4px;
`;
// Trust Indicators
const TrustSection = styled.section `
  margin-top: 80px;
  text-align: center;
  ${scrollReveal}
`;
const TrustGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  margin-top: 40px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;
const TrustItem = styled.div `
  padding: 32px 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: var(--accent-gold, #B8860B);
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
  }
`;
const TrustIcon = styled.div `
  width: 48px;
  height: 48px;
  margin: 0 auto 16px;
  color: var(--accent-gold, #B8860B);

  svg {
    width: 100%;
    height: 100%;
  }
`;
const TrustTitle = styled.h4 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 16px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
`;
const TrustText = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.6;
`;
// Icons
const ArrowRightIcon = () => (_jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M5 12h14M12 5l7 7-7 7" }) }));
const WhatsAppIcon = () => (_jsx("svg", { viewBox: "0 0 24 24", fill: "currentColor", children: _jsx("path", { d: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" }) }));
const PhoneIcon = () => (_jsx("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" }) }));
const MailIcon = () => (_jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" }), _jsx("polyline", { points: "22,6 12,13 2,6" })] }));
const ClockIcon = () => (_jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("polyline", { points: "12 6 12 12 16 14" })] }));
const MapPinIcon = () => (_jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" }), _jsx("circle", { cx: "12", cy: "10", r: "3" })] }));
// Custom hook for scroll animation
const useScrollReveal = () => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
                observer.disconnect();
            }
        }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
        if (ref.current) {
            observer.observe(ref.current);
        }
        return () => observer.disconnect();
    }, []);
    return { ref, isVisible };
};
const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        businessUrl: '',
        message: '',
        newsletter: false,
    });
    const formReveal = useScrollReveal();
    const trustReveal = useScrollReveal();
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };
    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked,
        }));
    };
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError('');
        try {
            const response = await apiClient.post('contact-inquiries/create', formData, { skipAuth: true });
            if (response.ok && response.data?.success) {
                setSubmitSuccess(true);
                setFormData({
                    name: '',
                    email: '',
                    businessUrl: '',
                    message: '',
                    newsletter: false,
                });
            }
            else {
                setSubmitError(response.error || 'Failed to submit inquiry. Please try again.');
            }
        }
        catch (error) {
            setSubmitError('Network error. Please check your connection and try again.');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    // Contact requests will be stored in database - admin will call back
    return (_jsxs(PageWrapper, { children: [_jsx(HeroSection, { children: _jsxs(HeroContent, { children: [_jsxs(HeroTitle, { children: ["Let's Start a ", _jsx("span", { children: "Conversation" })] }), _jsx(HeroSubtitle, { children: "We love our business partners, so feel free to reach out. Have a question about our diamonds or want to explore a partnership? We're always happy to connect." })] }) }), _jsxs(ContentWrapper, { children: [_jsxs("div", { ref: formReveal.ref, children: [_jsx(SectionLabel, { children: "Get In Touch" }), _jsx(SectionTitle, { children: "Send Us a Message" })] }), _jsxs(ContactGrid, { "$isVisible": formReveal.isVisible, children: [_jsx(FormSection, { children: _jsxs(StyledForm, { onSubmit: handleSubmit, children: [_jsxs(FormRow, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "name", children: "Full Name *" }), _jsx(Input, { type: "text", id: "name", name: "name", value: formData.name, onChange: handleInputChange, required: true, placeholder: "John Smith" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "email", children: "Email Address *" }), _jsx(Input, { type: "email", id: "email", name: "email", value: formData.email, onChange: handleInputChange, required: true, placeholder: "john@company.com" })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "businessUrl", children: "Business URL or Description" }), _jsx(Input, { type: "text", id: "businessUrl", name: "businessUrl", value: formData.businessUrl, onChange: handleInputChange, placeholder: "www.yourcompany.com" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "message", children: "How Can We Help?" }), _jsx(Textarea, { id: "message", name: "message", value: formData.message, onChange: handleInputChange, placeholder: "Tell us about your diamond and jewelry needs..." })] }), _jsx(CheckboxWrapper, { children: _jsxs(CheckboxLabel, { children: [_jsx("input", { type: "checkbox", name: "newsletter", checked: formData.newsletter, onChange: handleCheckboxChange }), "Sign up for our email list for inventory updates, promotions, and exclusive offers."] }) }), submitSuccess && (_jsx("div", { style: {
                                                padding: '16px',
                                                marginBottom: '16px',
                                                background: 'rgba(34, 197, 94, 0.1)',
                                                border: '1px solid rgba(34, 197, 94, 0.3)',
                                                borderRadius: '4px',
                                                color: 'var(--success-color)',
                                                textAlign: 'center'
                                            }, children: "\u2713 Thank you! We'll contact you within 24-48 hours." })), submitError && (_jsx("div", { style: {
                                                padding: '16px',
                                                marginBottom: '16px',
                                                background: 'rgba(239, 68, 68, 0.1)',
                                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                                borderRadius: '4px',
                                                color: '#ef4444',
                                                textAlign: 'center'
                                            }, children: submitError })), _jsxs(SubmitButton, { type: "submit", disabled: isSubmitting, children: [isSubmitting ? 'Sending...' : 'Send Message', !isSubmitting && _jsx(ArrowRightIcon, {})] })] }) }), _jsxs(Sidebar, { children: [_jsxs(InfoCard, { children: [_jsx(CardTitle, { children: "We'll Call You Back" }), _jsx(CardText, { children: "Submit your message and our team will review your inquiry. We'll contact you directly to discuss your diamond and jewelry needs." }), _jsxs(OfficeHours, { children: [_jsx(HoursLabel, { children: "Response Time" }), _jsx(HoursValue, { children: "Within 24-48 Hours" })] })] }), _jsxs(InfoCard, { children: [_jsx(CardTitle, { children: "What to Expect" }), _jsx(CardText, { style: { marginBottom: '12px' }, children: "\u2713 Personalized consultation" }), _jsx(CardText, { style: { marginBottom: '12px' }, children: "\u2713 Direct access to our experts" }), _jsx(CardText, { style: { marginBottom: '12px' }, children: "\u2713 Tailored diamond solutions" }), _jsx(CardText, { children: "\u2713 Confidential discussion" })] })] })] }), _jsxs(TrustSection, { ref: trustReveal.ref, "$isVisible": trustReveal.isVisible, children: [_jsx(SectionLabel, { children: "Why Choose Us" }), _jsxs(SectionTitle, { style: { textAlign: 'center' }, children: ["Here to Serve You", _jsx("span", { style: { display: 'block', margin: '12px auto 0', width: '60px', height: '2px', background: 'var(--accent-gold, #B8860B)' } })] }), _jsxs(TrustGrid, { children: [_jsxs(TrustItem, { children: [_jsx(TrustIcon, { children: _jsx(PhoneIcon, {}) }), _jsx(TrustTitle, { children: "We Call You" }), _jsx(TrustText, { children: "Submit your inquiry and we'll reach out to you directly for a personalized consultation." })] }), _jsxs(TrustItem, { children: [_jsx(TrustIcon, { children: _jsx(MailIcon, {}) }), _jsx(TrustTitle, { children: "Quick Response" }), _jsx(TrustText, { children: "We review and respond to all inquiries within 24-48 hours during business days." })] }), _jsxs(TrustItem, { children: [_jsx(TrustIcon, { children: _jsx(ClockIcon, {}) }), _jsx(TrustTitle, { children: "Expert Team" }), _jsx(TrustText, { children: "Connect directly with our diamond specialists for tailored solutions." })] }), _jsxs(TrustItem, { children: [_jsx(TrustIcon, { children: _jsx(MapPinIcon, {}) }), _jsx(TrustTitle, { children: "Secure & Confidential" }), _jsx(TrustText, { children: "All inquiries are handled with complete confidentiality and professionalism." })] })] })] })] })] }));
};
export default Contact;
